/**
 * UpdateActionEditForm.editRows — diff-row construction for
 * UpdateActionEditForm.
 *
 * Builds the DiffRow[] passed to DiffSection: read-only canonical labels on
 * both sides, with the active row (if any) swapped for its matching
 * `PropertyInput`/`AssociatedPipelineStageInput` editor. Extracted from the
 * component so the row-building logic — which touches every value source
 * (original values, backend enrichment, CRM store, stage resolution) — is
 * separated from the component's autosave/edit-target plumbing.
 */

import type { StageAssociationValue } from '../../lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import type { PropertySchemaMap } from '../../lib/crm/hooks/usePropertySchema';
import type { DiffRow } from './DiffSection';
import { EditableValueInput } from './EditableValueInput';
import { InlineEditField } from './InlineEditField';
import {
  DIFF_VALUE_MAX_LENGTH,
  ProposalPropertyValue,
} from './ProposalPropertyValue';
import { formatPropertyLabel } from './shared';
import {
  isStageProperty,
  propertyValueTextLength,
  resolveCurrentValue,
} from './UpdateActionEditForm.helpers';

export interface BuildEditRowsParams {
  propertyKeys: string[];
  originalValues: Record<string, unknown>;
  currentValues: Record<string, unknown> | undefined;
  crmFlatProps: Record<string, unknown> | undefined;
  currentStageValues: Record<string, string>;
  schema: PropertySchemaMap;
  orgId: number | undefined;
  activeEditKey: string | null;
  stageAssociationType: string | undefined;
  entityTypeName: string;
  editValues: Record<string, unknown>;
  handleChange: (key: string, value: unknown) => void;
  handleStageChange: (key: string, assoc: StageAssociationValue | null) => void;
  openEditor: (key: string) => void;
  handleCommit: () => void;
  handleCancelEdit: () => void;
}

/**
 * Build one DiffRow per property key: read-only canonical labels, with the
 * active row (if any) swapped for its input.
 */
export function buildEditRows({
  propertyKeys,
  originalValues,
  currentValues,
  crmFlatProps,
  currentStageValues,
  schema,
  orgId,
  activeEditKey,
  stageAssociationType,
  entityTypeName,
  editValues,
  handleChange,
  handleStageChange,
  openEditor,
  handleCommit,
  handleCancelEdit,
}: BuildEditRowsParams): DiffRow[] {
  return propertyKeys.map(key => {
    const currentRaw = resolveCurrentValue(key, {
      originalValues,
      currentValues,
      crmFlatProps,
      currentStageValues,
    });
    const propSchema = schema[key];
    const proposedRaw = editValues[key];
    const label = propSchema?.label || formatPropertyLabel(key);
    const isActive = activeEditKey === key;

    const row: DiffRow = {
      id: key,
      label,
      multiline: true,
      isExpandable:
        !isActive &&
        (propertyValueTextLength(currentRaw, propSchema?.field_type_name) >
          DIFF_VALUE_MAX_LENGTH ||
          propertyValueTextLength(proposedRaw, propSchema?.field_type_name) >
            DIFF_VALUE_MAX_LENGTH),
      currentValue: (
        <ProposalPropertyValue
          propertyKey={key}
          value={currentRaw}
          propertyType={propSchema}
          orgId={orgId}
        />
      ),
      proposedValue: (
        <ProposalPropertyValue
          propertyKey={key}
          value={proposedRaw}
          propertyType={propSchema}
          orgId={orgId}
        />
      ),
      onEditProposed: () => openEditor(key),
      editLabel: `Edit ${label}`,
    };

    if (!isActive) {
      return row;
    }

    return {
      ...row,
      isEditable: true,
      proposedValue: (
        <InlineEditField
          fieldTypeName={propSchema?.field_type_name}
          isMulti={propSchema?.is_multi}
          onCommit={handleCommit}
          onCancel={handleCancelEdit}
        >
          <EditableValueInput
            propertyKey={key}
            isStage={isStageProperty(key)}
            stageAssociationType={stageAssociationType}
            entityTypeName={entityTypeName}
            propSchema={propSchema}
            editValue={proposedRaw}
            onChange={handleChange}
            onStageChange={handleStageChange}
          />
        </InlineEditField>
      ),
    };
  });
}
